/**
 * Hooks for chain of custody management
 */

import { useState, useEffect, useCallback } from 'react'
import type {
  ChainOfCustodyEntry,
  CreateChainOfCustodyEntryRequest,
  ChainVerification,
  ListChainOfCustodyQuery,
} from '../types/case-container'
import {
  listChainOfCustody,
  countChainOfCustody,
  getEvidenceHistory,
  createChainOfCustodyEntry,
  verifyChain,
} from '../services/chain-of-custody'

// =============================================================================
// useChainOfCustody - List chain of custody entries
// =============================================================================

interface UseChainOfCustodyResult {
  entries: ChainOfCustodyEntry[]
  count: number
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch chain of custody entries for a case
 */
export function useChainOfCustody(
  caseId: string | null,
  params?: ListChainOfCustodyQuery
): UseChainOfCustodyResult {
  const [entries, setEntries] = useState<ChainOfCustodyEntry[]>([])
  const [count, setCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Destructure primitive fields from `params` so the dep array below depends on
  // stable scalar values, not on the object reference. Callers commonly pass a
  // fresh object literal (`{ page, perPage }`) every render — including the
  // object itself in the dep array recreates `fetch` on every render, which
  // in turn re-fires the `useEffect([fetch])` below, triggering an infinite
  // fetch loop that exhausts the browser's HTTP socket pool
  // (net::ERR_INSUFFICIENT_RESOURCES).
  const eventType = params?.eventType
  const evidenceType = params?.evidenceType
  const evidenceId = params?.evidenceId

  const fetch = useCallback(async () => {
    if (!caseId) {
      setEntries([])
      setCount(0)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Pass `params` through (not a reconstructed literal) to preserve the
      // shape's distinction between "absent" and "explicit undefined" under
      // exactOptionalPropertyTypes. Primitive fields are in the dep array
      // below so the closure captures fresh `params` whenever any field
      // actually changes.
      const [entriesData, countData] = await Promise.all([
        listChainOfCustody(caseId, params),
        countChainOfCustody(caseId),
      ])
      setEntries(entriesData)
      setCount(countData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch chain of custody')
      setEntries([])
      setCount(0)
    } finally {
      setLoading(false)
    }
    // Intentionally omits `params` (unstable object reference); we depend on
    // each primitive field instead. See the comment block above the destructuring.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, eventType, evidenceType, evidenceId])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { entries, count, loading, error, refetch: fetch }
}

// =============================================================================
// useEvidenceHistory - History for specific evidence item
// =============================================================================

interface UseEvidenceHistoryResult {
  entries: ChainOfCustodyEntry[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch chain of custody history for a specific evidence item
 */
export function useEvidenceHistory(
  caseId: string | null,
  evidenceType: string | null,
  evidenceId: string | null
): UseEvidenceHistoryResult {
  const [entries, setEntries] = useState<ChainOfCustodyEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!caseId || !evidenceType || !evidenceId) {
      setEntries([])
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const data = await getEvidenceHistory(caseId, evidenceType, evidenceId)
      setEntries(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch evidence history')
      setEntries([])
    } finally {
      setLoading(false)
    }
  }, [caseId, evidenceType, evidenceId])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { entries, loading, error, refetch: fetch }
}

// =============================================================================
// useChainVerification - Verify chain integrity
// =============================================================================

interface UseChainVerificationResult {
  verification: ChainVerification | null
  loading: boolean
  error: string | null
  verify: () => Promise<ChainVerification | null>
}

/**
 * Hook to verify chain of custody integrity
 */
export function useChainVerification(caseId: string | null): UseChainVerificationResult {
  const [verification, setVerification] = useState<ChainVerification | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const verify = useCallback(async (): Promise<ChainVerification | null> => {
    if (!caseId) {
      setVerification(null)
      return null
    }

    setLoading(true)
    setError(null)

    try {
      const data = await verifyChain(caseId)
      setVerification(data)
      return data
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to verify chain')
      setVerification(null)
      return null
    } finally {
      setLoading(false)
    }
  }, [caseId])

  return { verification, loading, error, verify }
}

// =============================================================================
// useChainOfCustodyMutations - Create entries
// =============================================================================

interface UseChainOfCustodyMutationsResult {
  creating: boolean
  error: string | null
  create: (
    caseId: string,
    entry: CreateChainOfCustodyEntryRequest
  ) => Promise<ChainOfCustodyEntry | null>
  clearError: () => void
}

/**
 * Hook for chain of custody mutations
 */
export function useChainOfCustodyMutations(): UseChainOfCustodyMutationsResult {
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const create = useCallback(
    async (
      caseId: string,
      entry: CreateChainOfCustodyEntryRequest
    ): Promise<ChainOfCustodyEntry | null> => {
      setCreating(true)
      setError(null)

      try {
        const result = await createChainOfCustodyEntry(caseId, entry)
        return result
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create chain of custody entry')
        return null
      } finally {
        setCreating(false)
      }
    },
    []
  )

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  return { creating, error, create, clearError }
}
